import { useState } from "react";
import "./top-navigation.style.scss";
import logoWhite from "../../assets/logo-white-BILLME.png";

// components
import ProfileModal from "../profile-modal/profile-modal.component";

// custom hooks
import { useOutsideClick } from "../../hooks/useOutsideClick";

// packages
import { useNavigate } from "react-router-dom";

// icons
import { FaCheckCircle, FaExclamationTriangle, FaUser } from "react-icons/fa";

// helper functions
import showToastMessage from "../../global/showToastMessage";

// queries
import { useCompanyDetails } from "../../queries/company.queries";
import type { Company } from "../../queries/company.queries";
import { useBillDates } from "../../queries/bill.queries";

// hooks
import { useIrdStatus } from "../../hooks/useIrdStatus";

interface TopNavProps {
	isSidenavOpen: boolean;
}

export default function TopNav({ isSidenavOpen }: TopNavProps) {
	const { irdStatus, checkStatus } = useIrdStatus();
	// This is the app-wide entry point that keeps company data warm — mounted
	// on every protected page via the Sidebar layout. React Query dedupes this
	// against every other useCompanyDetails() caller sharing the same queryKey.
	const { data } = useCompanyDetails();
	const companyDetails: Partial<Company> = data?.company || {};
	// Same app-wide entry point for bill/last/date — dedupes what used to be 4
	// separate getBillDates() mount-effect dispatches (top-nav, invoice-form,
	// invoice-bill.page, company-setting) into one shared query.
	const { data: billDates } = useBillDates();
	const failedIRDbillState = billDates?.failedBills ?? 0;
	const [isProfileVisible, setIsProfileVisible] = useState(false);

	const navigate = useNavigate();

	const toggleProfileModal = () => {
		setIsProfileVisible(!isProfileVisible);
	};

	const handleClickOutside = () => {
		setIsProfileVisible(false);
	};

	const ref = useOutsideClick(handleClickOutside);

	const handleCheckIRDStatus = async () => {
		//User can check if IRD servers are online
		await checkStatus();
	};

	return (
		<div className="topnav-container">
			<div className="topnav-container-inner">
				<div className="topnav-left-section">
					<img
						src={logoWhite}
						alt="logo"
						className={`logo-img ${isSidenavOpen ? "logo-rotate" : ""}`}
					/>
					<h1 className="logo-text">{companyDetails?.company_name}</h1>
				</div>
				<div className="topnav-right-section">
					{companyDetails?.company_data_corrupt ? (
						<div
							className="topnav-item"
							onClick={() =>
								showToastMessage(
									"Your account has some technical issues. Please contact your service providers.",
									"I",
								)
							}
						>
							Data Corrupt Status :
							<FaExclamationTriangle />
						</div>
					) : (
						<>
							{!irdStatus && (
								<div
									className="topnav-item"
									onClick={handleCheckIRDStatus}
								>
									IRD Status :
									{irdStatus ? (
										<FaCheckCircle />
									) : (
										<FaExclamationTriangle />
									)}
								</div>
							)}
							{failedIRDbillState > 0 && (
								<div
									className="topnav-item"
									onClick={() => navigate("/error-fix")}
								>
									<FaExclamationTriangle />
									Bill Sync Failure
									<span className="topnav-item-count">
										{failedIRDbillState}
									</span>
								</div>
							)}
						</>
					)}

					<div
						className="user-container"
						ref={ref}
						onClick={toggleProfileModal}
					>
						<FaUser />
					</div>
				</div>
				<ProfileModal isVisible={isProfileVisible} />
			</div>
		</div>
	);
}
